
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function NewlandsPage() {
  useEffect(() => {
    // Wrap all DOM manipulations in a try/catch to avoid uncaught errors in SSR or unexpected environments
    try {
      // Update page title and meta tags
      document.title =
        'Newlands Painters Cape Town | Premium Residential Painting Services | Heritage Home Specialists';

      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute(
          'content',
          'Premium painting services for Newlands heritage homes and residential properties. Expert craftsmanship for historic and modern homes with specialized restoration techniques.'
        );
      }

      // Update meta keywords
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute(
          'content',
          'Newlands painters, heritage home painting, residential painting, Newlands homes, Cape Town premium painters'
        );
      }

      // Update canonical URL
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute(
          'href',
          `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/newlands`
        );
      }

      // Add Schema.org JSON‑LD
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Newlands Painters - Premium Residential Painting Services',
        description:
          'Premium painting services for Newlands heritage homes and residential properties with specialized restoration techniques.',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/newlands`,
        mainEntity: {
          '@type': 'LocalBusiness',
          name: 'Cape Town Painters - Newlands',
          description:
            "Premium residential and heritage home painting services for Newlands' prestigious properties.",
          areaServed: 'Newlands, Cape Town',
          serviceType: 'Premium Residential and Heritage Painting Services',
        },
      });
      document.head.appendChild(script);
    } catch (err) {
      // Log the error but do not break the component rendering
      console.error('Error while setting page metadata for NewlandsPage:', err);
    }

    // Cleanup: remove the JSON‑LD script when the component unmounts
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Newlands')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Heritage Home Painting',
      description:
        "Specialized painting services for Newlands' historic homes, using traditional techniques and period-appropriate materials to preserve architectural integrity.",
      price: 'From R18,000',
      features: [
        'Heritage restoration',
        'Period techniques',
        'Authentic materials',
        'Architectural preservation',
        'Historical accuracy',
      ],
    },
    {
      icon: 'ri-building-line',
      title: 'Premium Residential',
      description:
        "High-end painting services for Newlands' luxury homes and modern properties, featuring premium materials and exceptional craftsmanship.",
      price: 'From R15,000',
      features: [
        'Premium materials',
        'Luxury finishes',
        'Custom colors',
        'Quality assurance',
        'Professional service',
      ],
    },
    {
      icon: 'ri-palette-line',
      title: 'Victorian Restoration',
      description:
        "Expert restoration painting for Newlands' Victorian and Edwardian properties, maintaining historical character while providing modern protection.",
      price: 'From R20,000',
      features: [
        'Victorian expertise',
        'Restoration techniques',
        'Character preservation',
        'Modern protection',
        'Heritage compliance',
      ],
    },
  ];

  const features = [
    {
      title: 'Heritage Specialists',
      description:
        'Extensive experience in heritage home restoration with deep understanding of historical painting techniques and period-appropriate materials and colors.',
    },
    {
      title: 'Premium Quality Focus',
      description:
        'Commitment to exceptional quality using premium materials and proven techniques that meet the high standards expected in Newlands\' prestigious properties.',
    },
    {
      title: 'Architectural Preservation',
      description:
        'Specialized knowledge in preserving and enhancing architectural features while ensuring modern protection and longevity for heritage properties.',
    },
    {
      title: 'Local Area Expertise',
      description:
        'Deep understanding of Newlands\' unique character, from historic homes to modern residences, ensuring appropriate finishes for each property type.',
    },
  ];

  const paintingProcess = [
    {
      step: 1,
      title: 'Heritage Assessment',
      description:
        'Comprehensive evaluation of your Newlands property including historical research, architectural analysis, and specialized planning for heritage homes.',
    },
    {
      step: 2,
      title: 'Specialized Preparation',
      description:
        'Careful preparation using appropriate techniques for heritage properties, including restoration of original features and protective treatments.',
    },
    {
      step: 3,
      title: 'Expert Application',
      description:
        "Professional application using premium materials and traditional techniques where appropriate, ensuring results that honor Newlands' heritage.",
    },
    {
      step: 4,
      title: 'Quality Assurance',
      description:
        'Thorough inspection and documentation ensuring every detail meets the exacting standards expected in Newlands\' prestigious properties.',
    },
  ];

  const testimonials = [
    {
      name: 'Robert Hamilton',
      property: 'Victorian Heritage Home',
      rating: 5,
      text: 'Exceptional work on our heritage home. The team understood the historical significance and delivered results that perfectly preserved the character while providing modern protection. Outstanding craftsmanship.',
      image:
        'https://public.readdy.ai/ai/img_res/5f333df7df350567a378900ae093e059.jpg',
    },
    {
      name: 'Catherine Williams',
      property: 'Modern Newlands Home',
      rating: 5,
      text: 'Outstanding service for our Newlands home. The quality of work was exceptional and the team was professional throughout. The results exceeded our expectations and the house looks beautiful.',
      image:
        'https://readdy.ai/api/search-image?query=Elegant%20homeowner%20at%20modern%20Newlands%20property%2C%20satisfied%20client%20portrait%2C%20upscale%20residential%20setting&width=80&height=80&seq=newlands2&orientation=squarish',
    },
  ];

  const projects = [
    {
      title: 'Victorian Manor Restoration',
      description:
        'Complete heritage restoration of a grand Victorian manor in Newlands, preserving original features while updating with modern protective systems.',
      image:
        'https://readdy.ai/api/search-image?query=Victorian%20manor%20house%20in%20Newlands%20with%20heritage%20restoration%2C%20historic%20architecture%2C%20professional%20painting%20work%2C%20mountain%20backdrop&width=400&height=300&seq=newlands3&orientation=landscape',
    },
    {
      title: 'Modern Family Estate',
      description:
        'Premium painting project for a contemporary Newlands family home, featuring luxury finishes and custom color schemes in a prestigious setting.',
      image:
        'https://readdy.ai/api/search-image?query=Modern%20luxury%20home%20in%20Newlands%20with%20mountain%20views%2C%20freshly%20painted%20exterior%2C%20upscale%20residential%20property&width=400&height=300&seq=newlands4&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20heritage%20property%20in%20Newlands%20with%20Table%20Mountain%20backdrop%2C%20Victorian%20architecture%2C%20painting%20equipment%2C%20historic%20home%20restoration%2C%20sunny%20day&width=1920&height=1080&seq=newlands_hero&orientation=landscape')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Newlands
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Premium painting services for Newlands heritage homes and residential
              properties. Expert craftsmanship for historic and modern homes with
              over 20 years of specialized experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Newlands Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Premium Painting Services for Newlands
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Newlands is one of Cape Town's most prestigious and historically
                significant suburbs, renowned for its heritage homes, tree-lined
                streets, and proximity to Table Mountain. This exclusive area
                features a remarkable collection of Victorian and Edwardian
                architecture alongside modern luxury properties.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our specialized team has extensive experience serving Newlands'
                unique property market, from heritage home restoration to luxury
                residential painting. We understand the delicate balance required
                between preserving historical authenticity and providing modern
                protection and beauty.
              </p>
              <p className="text-lg text-gray-600">
                Whether working on a Victorian manor, Edwardian cottage, or
                contemporary family home, our Newlands painting specialists
                deliver results that honor the area's distinguished character
                while ensuring superior protection and lasting beauty for your
                valuable property investment.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Newlands%20heritage%20suburb%20with%20Victorian%20homes%2C%20tree-lined%20streets%2C%20Table%20Mountain%20backdrop%2C%20prestigious%20residential%20area&width=600&height=400&seq=newlands5&orientation=landscape"
                alt="Newlands Heritage Area"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Newlands Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions for Newlands' heritage homes,
              luxury properties, and prestigious residential estates.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow"
              >
                <div
                  className="w-16 h-16 flex items-center justify-center mx-auto mb-6"
                  style={{ backgroundColor: '#ffff00' }}
                >
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">
                  {service.price}
                </div>
                <ul className="text-sm text-gray-600 space-y-1">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us for Newlands?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized expertise in heritage restoration and premium
              residential painting with exceptional quality standards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Newlands Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A specialized approach developed for Newlands' unique heritage
              properties and premium residential standards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div
                  className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold"
                  style={{ backgroundColor: '#ffff00' }}
                >
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {process.title}
                </h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Newlands Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our recent heritage restoration and premium residential
              painting projects across Newlands.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {project.title}
                  </h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Testimonials from satisfied Newlands property owners.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i
                      key={i}
                      className="ri-star-fill text-yellow-400 text-lg"
                    ></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">
                  &quot;{testimonial.text}&quot;
                </p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {testimonial.name}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {testimonial.property}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready for Premium Painting in Newlands?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's heritage home and premium residential painting
            specialists. Experience exceptional craftsmanship for your Newlands
            property.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="/contact"
              className="bg-teal-600 text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </a>
            <a
              href="mailto:Get a quote"
              className="bg-black text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Email Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
